import { useTranslation } from 'react-i18next';
import type { AdminVoucher } from '../../../api/voucher/voucherApiTypes';
import RowActionsMenu from '../../../components/RowActionsMenu';
import { tableCellClass } from '../../../components/TableCard';

// The actions cell of a promo / goodwill row. Module-level component (not a
// helper inside the page) so its branching does not count into the page
// component's cognitive complexity; its own file so its state matrix is
// testable without mounting the page.
const CodeActionsCell = ({
    voucher: v,
    isMaster,
    canManage,
    onCancel,
    onEdit,
    onDelete,
}: {
    voucher: AdminVoucher;
    isMaster: boolean;
    canManage: boolean;
    onCancel: (v: AdminVoucher) => void;
    onEdit: (v: AdminVoucher) => void;
    onDelete: (v: AdminVoucher) => void;
}) => {
    const { t } = useTranslation();
    // Edit only makes sense on an unused code; the hard delete is master-only
    // and also needs an unused code. Cancel (soft-disable) is available to
    // manage:vouchers — even for a used code, to stop further use. All three
    // change something, so they sit in the row's menu (design-decisions.md ›
    // Zeilenaktionen in Tabellen); the menu does not toggle the row's
    // redemption history. Cancel cannot be undone, so it sits with delete
    // after the divider.
    const unused = v.usedCounter === 0;
    const active = !v.archivedAt;
    return (
        <td className={tableCellClass}>
            <div className={'flex gap-3 items-center'}>
                {/* A cancelled code stays listed as deactivated instead of
                    vanishing (innodom review 7.2); nothing left to do on it
                    except a master hard-delete while unused. */}
                {!active && (
                    <span className={'text-foreground/50'}>
                        {t('vouchers_codes.deactivated')}
                    </span>
                )}
                <RowActionsMenu
                    rowName={v.code ?? t('common.missing')}
                    items={[
                        {
                            id: 'edit',
                            label: t('common.edit'),
                            onClick: () => onEdit(v),
                            hidden: !active || !unused || !canManage,
                        },
                    ]}
                    destructiveItems={[
                        {
                            id: 'cancel',
                            label: t('vouchers_codes.cancel_code.action'),
                            onClick: () => onCancel(v),
                            hidden: !active || !canManage,
                        },
                        {
                            id: 'delete',
                            label: t('common.delete'),
                            onClick: () => onDelete(v),
                            hidden: !unused || !isMaster,
                        },
                    ]}
                />
            </div>
        </td>
    );
};

export default CodeActionsCell;
